/**
 * Sessions repo — Dexie reads/writes for ski sessions.
 *
 * Every session row carries:
 *   - userId        — anon-${uuid} or Cognito sub (used for migration on sign-in)
 *   - syncStatus    — 'local' (awaiting push) | 'syncing' | 'synced' | 'error'
 *   - lastUpdatedAt — ms epoch, used for last-write-wins conflict resolution
 *   - deletedAt     — soft-delete tombstone (ms epoch)
 */

import { db, SYNC_STATUS } from './index';

/** @returns {Promise<Array>} sessions for the given user (excluding tombstones) */
export async function listSessions(userId, { season, limit } = {}) {
  let coll = db.sessions.where('userId').equals(userId);
  let rows = await coll.toArray();
  rows = rows.filter((s) => !s.deletedAt);
  if (season) {
    rows = rows.filter((s) => s.season === season);
  }
  // Most recent first
  rows.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
  if (limit) rows = rows.slice(0, limit);
  return rows;
}

export async function getSession(id) {
  const row = await db.sessions.get(id);
  if (!row || row.deletedAt) return null;
  return row;
}

export async function createLocalSession(session, { userId, track }) {
  const now = Date.now();
  const row = {
    ...session,
    userId,
    syncStatus: SYNC_STATUS.LOCAL,
    lastUpdatedAt: now,
    deletedAt: null,
  };
  await db.transaction('rw', db.sessions, db.tracks, async () => {
    await db.sessions.put(row);
    if (Array.isArray(track) && track.length > 0) {
      await db.tracks.put({ sessionId: session.id, userId, points: track });
    }
  });
  return row;
}

export async function updateLocalSession(id, updates) {
  const existing = await db.sessions.get(id);
  if (!existing) return null;
  const merged = {
    ...existing,
    ...updates,
    lastUpdatedAt: Date.now(),
    syncStatus: existing.syncStatus === SYNC_STATUS.SYNCED ? SYNC_STATUS.LOCAL : existing.syncStatus,
  };
  await db.sessions.put(merged);
  return merged;
}

export async function softDeleteSession(id) {
  const existing = await db.sessions.get(id);
  if (!existing) return null;
  const merged = {
    ...existing,
    deletedAt: Date.now(),
    lastUpdatedAt: Date.now(),
    syncStatus: SYNC_STATUS.LOCAL,
  };
  await db.sessions.put(merged);
  return merged;
}

/**
 * Upsert a session from the server. Skips rows with local pending changes
 * (LWW would lose them — the push will reconcile instead).
 */
export async function upsertFromServer(serverSession, userId) {
  const existing = await db.sessions.get(serverSession.id);
  if (existing && existing.syncStatus === SYNC_STATUS.LOCAL) {
    return existing; // local pending — let the push reconcile
  }
  const row = {
    ...serverSession,
    userId,
    syncStatus: SYNC_STATUS.SYNCED,
    lastUpdatedAt: Date.now(),
    deletedAt: null,
  };
  await db.sessions.put(row);
  return row;
}

export async function markSynced(id) {
  const existing = await db.sessions.get(id);
  if (!existing) return null;
  await db.sessions.put({ ...existing, syncStatus: SYNC_STATUS.SYNCED });
}

export async function markSyncError(id, error) {
  const existing = await db.sessions.get(id);
  if (!existing) return null;
  await db.sessions.put({
    ...existing,
    syncStatus: SYNC_STATUS.ERROR,
    lastSyncError: error?.message || String(error),
  });
}

export async function getTrack(sessionId) {
  const row = await db.tracks.get(sessionId);
  return row?.points || null;
}

export async function setTrack(sessionId, points, userId) {
  await db.tracks.put({ sessionId, userId, points });
}

/**
 * Rekey all rows owned by `fromUserId` to `toUserId`. Used when an anonymous
 * user signs in — every local row gets the Cognito sub stamped on it.
 *
 * Returns the number of rows updated.
 */
export async function rekeyUser(fromUserId, toUserId) {
  return db.transaction('rw', db.sessions, db.tracks, async () => {
    const sessions = await db.sessions.where('userId').equals(fromUserId).toArray();
    for (const s of sessions) {
      await db.sessions.put({ ...s, userId: toUserId, lastUpdatedAt: Date.now() });
      const track = await db.tracks.get(s.id);
      if (track) {
        await db.tracks.put({ ...track, userId: toUserId });
      }
    }
    return sessions.length;
  });
}

/** Aggregate season totals (count unique startDates). */
export async function getSeasonTotals(userId) {
  const rows = await listSessions(userId);
  let totalRuns = 0;
  let totalVertical = 0;
  let totalDistance = 0;
  const uniqueDays = new Set();
  for (const s of rows) {
    if (s.stats) {
      totalRuns += s.stats.totalRuns || 0;
      totalVertical += s.stats.totalVertical || 0;
      totalDistance += s.stats.totalDistance || 0;
    }
    if (s.startTime) {
      uniqueDays.add(new Date(s.startTime).toISOString().split('T')[0]);
    }
  }
  return { totalRuns, totalVertical, totalDistance, totalDays: uniqueDays.size };
}
